// Companion/blocks/VideoBlock.tsx
import { VideoBlockBody } from './bodies/VideoBlockBody/VideoBlockBody';
import type { ChatVideoBlockPayload } from '@/types/chatBlocks';
import type { ChatSurface } from '@/types/chatEnvelope';
import {
  CHAT_ANCHOR_LABEL_TITLE_MAX_CHARS,
  toChatAnchorLabel,
} from '@/chatContext/chatAnchorLabels';
import { useBlockAnchor } from './useBlockAnchor';

/**
 * A video served into the thread, at the second the answer is at.
 *
 * The ported `VideoBlockBody` fetches the full video (versions, live questions)
 * from its id and plays it; the payload's fields are only the fallback for when
 * that lookup fails. On the page it plays inline; in the drawer and the
 * document split it stays the compact card that opens the viewer — an inline
 * player in a 26rem column is unreadable — and both paths seek to the start
 * second.
 */
interface VideoBlockProps {
  payload: ChatVideoBlockPayload;
  surface: ChatSurface;
  isReadOnly: boolean;
}

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const VideoBlock = ({ payload, surface, isReadOnly }: VideoBlockProps) => {
  const { claim } = useBlockAnchor(isReadOnly);

  const item = {
    id: payload.videoId,
    title: payload.title ?? '',
    description: payload.description ?? '',
    thumbnailUrl: payload.thumbnailUrl ?? '',
  };

  return (
    <VideoBlockBody
      item={item}
      videoDisplay={surface === 'chat_page' ? 'inline' : 'card'}
      startSecond={payload.startSecond ?? undefined}
      onAnchorChange={(anchor) => {
        // Report what the player actually told us. The video the block was
        // built from is only the fallback: the body resolves the real
        // video before it reports, and a failed lookup can leave it with a
        // different id or none at all.
        const video = (anchor?.video ?? {}) as Record<string, unknown>;

        const videoId =
          typeof anchor?.id === 'string' && anchor.id
            ? anchor.id
            : payload.videoId;

        if (!videoId) return;

        claim({
          type: 'video',
          // The player's own position when it has one, else where we asked it
          // to start.
          timestampSec:
            asNumber(video.timestamp_sec) ?? payload.startSecond ?? null,
          videoId,
          // The resolved video's title, for the same reason as the id above:
          // the payload's is only the fallback (ORA-662).
          label: toChatAnchorLabel(
            'Video',
            typeof anchor?.title === 'string' && anchor.title
              ? anchor.title
              : payload.title,
            CHAT_ANCHOR_LABEL_TITLE_MAX_CHARS
          ),
          // The body reports an anchor only on engagement — the inline
          // player's `onEngaged`, or the viewer being opened — so by the time
          // this runs the student has started watching.
          playing: true,
        });
      }}
    />
  );
};

export default VideoBlock;
